import { Link } from 'react-router-dom';
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useBlogPosts, useBlogFeaturedImage } from '@/hooks/useBlog';
import { blogService, WordPressPost } from '@/services/blogService';
import { relatedPostsService } from '@/services/relatedPosts';

interface RelatedPostsProps {
  currentPost: WordPressPost;
}

const RelatedPostCard = ({ post }: { post: WordPressPost }) => {
  const { data: featuredImageUrl } = useBlogFeaturedImage(post);
  const readingTime = Math.ceil(blogService.stripHtml(post.content.rendered).split(' ').length / 200);

  return (
    <Card className="overflow-hidden group hover:shadow-md transition-shadow">
      <div className="aspect-video overflow-hidden">
        <img 
          src={featuredImageUrl || "/api/placeholder/300/200"} 
          alt={blogService.stripHtml(post.title.rendered)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
          <Link to={`/blog/${post.slug}`}>
            {blogService.stripHtml(post.title.rendered)}
          </Link>
        </CardTitle>
        <CardDescription className="line-clamp-2 text-sm">
          {blogService.stripHtml(post.excerpt.rendered)}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center text-xs text-muted-foreground space-x-4">
          <div className="flex items-center">
            <CalendarIcon className="w-3 h-3 mr-1" />
            <span>{new Date(post.date).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center">
            <ClockIcon className="w-3 h-3 mr-1" />
            <span>{readingTime} min</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RelatedPosts = ({ currentPost }: RelatedPostsProps) => {
  const { data: postsData } = useBlogPosts({ per_page: 20 });
  
  if (!postsData?.posts) return null;

  const relatedPosts = relatedPostsService.findRelatedPosts({
    currentPost,
    allPosts: postsData.posts,
    maxResults: 3
  });

  if (relatedPosts.length === 0) return null;

  return (
    <section className="mt-16 pt-8 border-t">
      <h3 className="text-2xl font-bold text-foreground mb-8">Related Articles</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <RelatedPostCard key={post.id} post={post} />
        ))}
      </div>
    </section>
  );
};

export default RelatedPosts;